import { useTheme } from '@ecom/ui-kit';
import { type ReactElement, useLayoutEffect, useState } from 'react';
import { NavigationType, Outlet, useLocation, useNavigationType } from 'react-router-dom';

import { CommandPalette } from './CommandPalette';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

import { AssistantPanel } from '@/features/assistant/components/AssistantPanel';
import { LastSignInNotice } from '@/features/my-account/components/LastSignInNotice';
import { NewOrderAlerts } from '@/features/orders/components/NewOrderAlerts';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { LiveStatusContext } from '@/lib/live-status-context';
import { useLiveUpdates } from '@/lib/live-updates';

const COLLAPSED_KEY = 'souk-merchant-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * `useTheme()` and `useActiveShop()` are each called exactly once here and
 * passed down, rather than separately in every consumer. `@ecom/ui-kit`'s
 * theme hook has no cross-instance sync (confirmed live in Lot 1: two
 * independent instances desynced the moment one of them toggled). The same
 * risk applies to `useActiveShop()` for a different reason: it owns an
 * effect that auto-selects a shop, and it queries `/auth/me/memberships` —
 * a second instance would refetch redundantly and could, in principle,
 * race its own auto-select effect against a sibling's.
 */
export function AppShell(): ReactElement {
  const theme = useTheme();
  const shop = useActiveShop();
  // Migration 0098 — one live stream for the whole shell: every screen refreshes by itself.
  const liveStatus = useLiveUpdates();
  // Below 900px the sidebar slides in as a drawer from the top bar's menu button.
  const [navOpen, setNavOpen] = useState(false);
  // Desktop: the sidebar folds to a 74px icon rail; the choice is remembered in this browser only.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  // A screen opened from the menu or a link starts at its top: the page itself scrolls, and a client-side navigation
  // would otherwise keep the previous screen's scroll and open a list half-way down. Back/forward (POP) keeps the
  // browser's own restoration, so returning to a list lands where the merchant left it.
  useLayoutEffect(() => {
    if (navigationType !== NavigationType.Pop) window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  const toggleCollapsed = (): void => {
    setCollapsed((value) => {
      const next = !value;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Storage refused (private window): the choice simply lasts until the page is reloaded.
      }
      return next;
    });
  };

  return (
    // « Souk 2.0 » (Figma): a fixed midnight sidebar, the workspace beside it with a sticky translucent top bar; the
    // page itself scrolls (globals.css `.sk-*`).
    <div className={`sk-shell ${collapsed ? 'is-collapsed' : ''}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        mobileOpen={navOpen}
        onCloseMobile={() => {
          setNavOpen(false);
        }}
      />
      <div className="sk-workspace">
        <Topbar
          theme={theme}
          shop={shop}
          liveStatus={liveStatus}
          navOpen={navOpen}
          onOpenNav={() => {
            setNavOpen(true);
          }}
        />
        <LastSignInNotice />
        {/* The bottom space lets the last button scroll clear of the iPhone home bar. */}
        <main className="sk-main">
          <LiveStatusContext.Provider value={liveStatus}>
            {/* Each screen settles in once when it opens (200 ms, 4 px): keyed on the path, so filters and live updates
              inside a screen never replay it. */}
            <div key={pathname} className="page-enter">
              <Outlet />
            </div>
          </LiveStatusContext.Provider>
        </main>
      </div>
      {/* « Nouvelle commande » on every screen (fed by the live stream above). */}
      <NewOrderAlerts />
      <CommandPalette theme={theme} />
      <AssistantPanel />
    </div>
  );
}
